import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Input } from '@/shared/ui/inputs/Input';
import { Textarea } from '@/shared/ui/inputs/Textarea';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiFaqPost, apiFaqPut } from '@/entities/faq';
import { faqSchema, type FaqFormValues } from '@/features/faqs/modules/types';
import type { ApiFaqGetItem } from '@/shared/types/api/faqs';

type Props = {
  open: boolean;
  editing: ApiFaqGetItem | null;
  onClose: () => void;
};

export const FeaturesFaqsFaqFormDialog = ({ open, editing, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const form = useForm<FaqFormValues>({
    resolver: zodResolver(faqSchema),
    defaultValues: {
      question_uz: '',
      question_ru: '',
      question_en: '',
      question_kk: '',
      answer_uz: '',
      answer_ru: '',
      answer_en: '',
      answer_kk: '',
      order: 0,
      is_active: true,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        question_uz: editing?.question_uz ?? '',
        question_ru: editing?.question_ru ?? '',
        question_en: editing?.question_en ?? '',
        question_kk: editing?.question_kk ?? '',
        answer_uz: editing?.answer_uz ?? '',
        answer_ru: editing?.answer_ru ?? '',
        answer_en: editing?.answer_en ?? '',
        answer_kk: editing?.answer_kk ?? '',
        order: editing?.order ?? 0,
        is_active: editing?.is_active ?? true,
      });
    }
  }, [open, editing, form]);

  const create = useMutate({
    api: (v: FaqFormValues) => apiFaqPost(v),
    invalidateKeys: [queryKeys.faqs.all],
    onSuccess: () => {
      toast.success(t('faqs.toast.created'));
      onClose();
    },
    onError: (_e, m) => toast.error(m),
  });

  const update = useMutate({
    api: (v: FaqFormValues) => apiFaqPut(editing?.id ?? '', v),
    invalidateKeys: [queryKeys.faqs.all],
    onSuccess: () => {
      toast.success(t('faqs.toast.updated'));
      onClose();
    },
    onError: (_e, m) => toast.error(m),
  });

  const isPending = create.isPending || update.isPending;
  const e = form.formState.errors;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="xl"
      title={editing ? t('faqs.form.editTitle') : t('faqs.form.createTitle')}
      description={t('faqs.form.description')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            loading={isPending}
            onClick={form.handleSubmit((v) => (editing ? update.mutate(v) : create.mutate(v)))}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label={t('faqs.form.questionUz')}
            required
            autoFocus
            error={e.question_uz?.message ? t(e.question_uz.message) : undefined}
            {...form.register('question_uz')}
          />
          <Input
            label={t('faqs.form.questionRu')}
            error={e.question_ru?.message ? t(e.question_ru.message) : undefined}
            {...form.register('question_ru')}
          />
          <Input
            label={t('faqs.form.questionEn')}
            error={e.question_en?.message ? t(e.question_en.message) : undefined}
            {...form.register('question_en')}
          />
          <Input
            label={t('faqs.form.questionKk')}
            error={e.question_kk?.message ? t(e.question_kk.message) : undefined}
            {...form.register('question_kk')}
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Textarea
            label={t('faqs.form.answerUz')}
            required
            rows={4}
            error={e.answer_uz?.message ? t(e.answer_uz.message) : undefined}
            {...form.register('answer_uz')}
          />
          <Textarea
            label={t('faqs.form.answerRu')}
            rows={4}
            error={e.answer_ru?.message ? t(e.answer_ru.message) : undefined}
            {...form.register('answer_ru')}
          />
          <Textarea
            label={t('faqs.form.answerEn')}
            rows={4}
            error={e.answer_en?.message ? t(e.answer_en.message) : undefined}
            {...form.register('answer_en')}
          />
          <Textarea
            label={t('faqs.form.answerKk')}
            rows={4}
            error={e.answer_kk?.message ? t(e.answer_kk.message) : undefined}
            {...form.register('answer_kk')}
          />
        </div>
        <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-2">
          <Input
            label={t('faqs.form.order')}
            type="number"
            inputMode="numeric"
            error={e.order?.message ? t(e.order.message) : undefined}
            {...form.register('order')}
          />
          <Controller
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <label className="flex cursor-pointer items-center gap-2 self-end pb-2.5 text-sm font-medium text-ink-soft">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  checked={field.value}
                  onChange={(ev) => field.onChange(ev.target.checked)}
                  onBlur={field.onBlur}
                />
                {t('faqs.form.isActive')}
              </label>
            )}
          />
        </div>
      </div>
    </Dialog>
  );
};
